/**
 * Security Utilities
 * XSS prevention and input sanitization helpers
 */

import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * Use this for any user-generated content that needs to be rendered as HTML
 */
export const sanitizeHTML = (dirty: string): string => {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br'],
    ALLOWED_ATTR: [],
  });
};

/**
 * Sanitize user input text (strips all HTML)
 * Use this for form inputs and text fields
 */
export const sanitizeText = (input: string): string => {
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

/**
 * Validate and sanitize email addresses
 */
export const sanitizeEmail = (email: string): string => {
  const sanitized = sanitizeText(email.trim().toLowerCase());
  // Basic email validation
  const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
  if (!emailRegex.test(sanitized)) {
    return '';
  }
  return sanitized;
};

/**
 * Validate and sanitize phone numbers
 */
export const sanitizePhone = (phone: string): string => {
  // Remove all non-numeric characters except + at start
  const sanitized = sanitizeText(phone);
  return sanitized.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
};

/**
 * Sanitize URL to prevent javascript: and data: URLs
 */
export const sanitizeURL = (url: string): string => {
  const sanitized = sanitizeText(url.trim());
  
  // Block dangerous URL schemes
  // eslint-disable-next-line no-script-url
  const dangerousSchemes = ['javascript:', 'data:', 'vbscript:', 'file:'];
  const lowerURL = sanitized.toLowerCase();
  
  for (const scheme of dangerousSchemes) {
    if (lowerURL.startsWith(scheme)) {
      return '#';
    }
  }
  
  // Only allow http, https, and relative URLs
  if (sanitized.startsWith('http://') || 
      sanitized.startsWith('https://') || 
      sanitized.startsWith('/') ||
      sanitized.startsWith('#')) {
    return sanitized;
  }
  
  return '#';
};

/**
 * Escape HTML special characters
 * Use this when you need to display user input as plain text
 */
export const escapeHTML = (text: string): string => {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  return text.replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * Validate that a string doesn't contain suspicious patterns
 */
export const detectXSSPatterns = (input: string): boolean => {
  const xssPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i, // Event handlers like onclick=
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /eval\(/i,
    /expression\(/i,
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
};

/**
 * Sanitize form data object
 */
export const sanitizeFormData = <T extends Record<string, any>>(data: T): T => {
  const sanitized = {} as T;
  
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      (sanitized as any)[key] = sanitizeText(value);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      (sanitized as any)[key] = value;
    } else if (value === null || value === undefined) {
      (sanitized as any)[key] = value;
    } else if (typeof value === 'object' && value !== null) {
      (sanitized as any)[key] = sanitizeFormData(value);
    }
  }
  
  return sanitized;
};

/**
 * Create a safe notification element without innerHTML
 */
export const createSafeNotification = (config: {
  title: string;
  message: string;
  type?: 'info' | 'success' | 'error' | 'warning';
  onConfirm?: () => void;
  onDismiss?: () => void;
  confirmText?: string;
  dismissText?: string;
}): HTMLDivElement => {
  const { title, message, type = 'info', onConfirm, onDismiss, confirmText, dismissText } = config;
  
  // Color schemes for different types
  const colors = {
    info: { bg: '#3b82f6', text: 'white' },
    success: { bg: '#10b981', text: 'white' },
    error: { bg: '#ef4444', text: 'white' },
    warning: { bg: '#f59e0b', text: 'white' },
  };
  
  const color = colors[type];
  
  // Create container
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: ${color.bg};
    color: ${color.text};
    padding: 16px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 1000;
    max-width: 300px;
  `;
  
  // Create title
  const titleEl = document.createElement('p');
  titleEl.textContent = sanitizeText(title);
  titleEl.style.cssText = 'margin: 0 0 8px 0; font-weight: 500;';
  container.appendChild(titleEl);
  
  // Create message
  const messageEl = document.createElement('p');
  messageEl.textContent = sanitizeText(message);
  messageEl.style.cssText = 'margin: 0 0 12px 0; font-size: 14px; opacity: 0.9;';
  container.appendChild(messageEl);
  
  // Create button container
  const buttonContainer = document.createElement('div');
  
  // Create confirm button if callback provided
  if (onConfirm) {
    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = confirmText || 'Confirmar';
    confirmBtn.style.cssText = `
      background: white;
      color: ${color.bg};
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      font-weight: 500;
      cursor: pointer;
      margin-right: 8px;
    `;
    confirmBtn.addEventListener('click', () => {
      onConfirm();
      container.remove();
    });
    buttonContainer.appendChild(confirmBtn);
  }
  
  // Create dismiss button
  const dismissBtn = document.createElement('button');
  dismissBtn.textContent = dismissText || (onConfirm ? 'Cancelar' : 'OK');
  dismissBtn.style.cssText = `
    background: transparent;
    color: ${color.text};
    border: 1px solid rgba(255,255,255,0.3);
    padding: 8px 16px;
    border-radius: 4px;
    font-weight: 500;
    cursor: pointer;
  `;
  dismissBtn.addEventListener('click', () => {
    if (onDismiss) onDismiss();
    container.remove();
  });
  buttonContainer.appendChild(dismissBtn);
  
  container.appendChild(buttonContainer);
  
  return container;
};

